import { assignInlineVars } from '@vanilla-extract/dynamic';
import { Property } from 'csstype';

import { ComponentPropsWithoutStyles } from '@/util/react/ComponentPropsWithoutStyles';

import * as styles from './row.css';

type InternalProps<E extends React.ElementType> = {
  as?: E;
  justifyContent?: Property.JustifyContent;
  alignItems?: Property.AlignItems;
  flexWrap?: Property.FlexWrap;
  spacing?: Exclude<Property.Gap, 0>;
  xSpacing?: Exclude<Property.ColumnGap, 0>;
  ySpacing?: Exclude<Property.RowGap, 0>;
  fill?: boolean;
  width?: Exclude<Property.Width, 0>;
  minWidth?: Exclude<Property.MinWidth, 0>;
  maxWidth?: Exclude<Property.MaxWidth, 0>;
} & ComponentPropsWithoutStyles<React.HTMLAttributes<HTMLDivElement>>;

export type RowProps<E extends React.ElementType> = InternalProps<E> &
  Omit<React.ComponentProps<E>, keyof InternalProps<E>>;

export const Row = <E extends React.ElementType = 'div'>({
  as,
  justifyContent,
  alignItems,
  flexWrap,
  spacing,
  xSpacing,
  ySpacing,
  fill = true,
  width,
  minWidth,
  maxWidth,
  children,
}: RowProps<E>) => {
  const TagName = as || 'div';

  return (
    <TagName
      className={fill ? styles.root.filled : styles.root.custom}
      style={{
        ...assignInlineVars({
          [styles.rowJustifyContentVar]: justifyContent ?? 'start',
          [styles.rowAlignItemsVar]: alignItems ?? 'center',
          [styles.flexWrapVar]: flexWrap ?? 'nowrap',
          [styles.rowXSpacingVar]: xSpacing ?? spacing ?? '0',
          [styles.rowYSpacingVar]: ySpacing ?? spacing ?? '0',
          [styles.widthVar]: width ?? 'unset',
          [styles.minWidthVar]: minWidth ?? 'unset',
          [styles.maxWidthVar]: maxWidth ?? 'unset',
        }),
      }}
    >
      {children}
    </TagName>
  );
};
